import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { JSONApiDataObject, SetupIntent, StripeSetupIntent } from 'Douleutaras/types/index';
import { RootState } from 'Douleutaras/redux/store';
import normalize from 'json-api-normalizer';

const ENDPOINT_SETUP_INTENT = 'api/public/v3/setup-intent/';

const setupIntentAdapter = createEntityAdapter<JSONApiDataObject<StripeSetupIntent>>({
  selectId: cat => cat.id
});

export const createSetupIntent = createAsyncThunk(
  'payment/stripe/setup-intent/create',
  async ({ paymentMethodId, usage, product_sku }: SetupIntent,
         api) => {
      const response = await makeRequest(
        ENDPOINT_SETUP_INTENT,
        {
          method: 'POST',
          body: {
            data: {
              type: 'unattendedRegistration',
              attributes: {
                paymentMethodId,
                usage,
                product_sku
              }
            }
          }
        }
      )(api);

      return response;
  }
);

export const cancelSetupIntent = createAsyncThunk(
  'payment/stripe/setup-intent/delete',
  async ({ setupIntentId }: { setupIntentId: string }, api) => {
    const response = await makeRequest(
      ENDPOINT_SETUP_INTENT + setupIntentId,
      {
        method: 'DELETE'
      }
    )(api);

    if (![200, 201].includes(response.status) || response.data.data.status !== 'ok') {
      return api.rejectWithValue(response.payload?.message);
    }

    return setupIntentId;
  }
);

export const fetchSetupIntents = createAsyncThunk(
  'payment/stripe/setup-intent/fetch',
  async (args, api) => {
    const response = await makeRequest(
      ENDPOINT_SETUP_INTENT,
      {
        method: 'GET',
        additionalGetParams: {}
      }
    )(api);

    return normalize(response.data, { ENDPOINT_SETUP_INTENT });
  }
);

export const stripeSlice = createSlice({
  name: 'stripe',
  initialState: setupIntentAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSetupIntents.fulfilled, (state, action) => {
      setupIntentAdapter.upsertMany(state, action.payload.unattendedRegistration || []);
    });
    builder.addCase(cancelSetupIntent.fulfilled, (state, action) => {
      setupIntentAdapter.removeOne(state, action.payload);
    });
  }
});

export const stripeSetupIntentsSelector = setupIntentAdapter.getSelectors((state: RootState) => state.payments.stripeAuto);
